<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  bindLiveSitePlacement,
  buildCompatibilityClassList,
  detectLiveSiteCompatibility,
  resolveCompatibilityConfig
} from "../liveSiteCompatibility";
import { renderMessageHtml } from "../protocol";
import { useChatClient } from "../useChatClient";
import { useLauncherIcon, DEFAULT_LAUNCHER_ICON_SOURCES } from "../useLauncherIcon";
import type { ChatbotConfig } from "../types";

const props = defineProps<{
  config: ChatbotConfig;
}>();

const open = ref(false);
const draft = ref("");
const rootRef = ref<HTMLElement | null>(null);
const messagesRef = ref<HTMLElement | null>(null);
const copiedMessageId = ref<string | null>(null);
const showGreeting = ref(false);
const client = useChatClient(props.config);
const launcherIconSources = props.config.launcherIconUrl
  ? [props.config.launcherIconUrl, ...DEFAULT_LAUNCHER_ICON_SOURCES]
  : DEFAULT_LAUNCHER_ICON_SOURCES;
const launcherIcon = useLauncherIcon({ sources: launcherIconSources });
const launcherIconState = launcherIcon.state;
const compatibilityConfig = resolveCompatibilityConfig(props.config);
const compatibility = ref(
  typeof document === "undefined"
    ? {
        hasSocialChat: false,
        hasBackToTop: false,
        themeProfile: compatibilityConfig.themeProfile
      }
    : detectLiveSiteCompatibility(document, compatibilityConfig)
);
let compatibilityObserver: MutationObserver | null = null;
let placementCleanup: (() => void) | null = null;

const title = computed(() => props.config.copy?.title || "Prestech");
const subtitle = computed(() => props.config.copy?.subtitle || "Atendimento automatizado");
const canSend = computed(() => draft.value.trim().length > 0 && !client.pending.value);
const fallbackRobotGlyph = "\u{1F916}";

const greetingPopup = computed(() => props.config.templates?.cwGreetingPopup || "Olá! 👋 Se precisar de ajuda, fale comigo! 🦾");
const initialMessage = computed(() => props.config.templates?.cwInitialMessage || "Olá! 👋\n\nSou o chatbot da Prestech. Posso te ajudar a encontrar o atendimento certo. 🦾\n\nEnvie *MENU* para ver as opções ou descreva sua necessidade em uma mensagem curta.");
const thinkingText = computed(() => props.config.templates?.cwThinking || "Pensando...");
const handoffIntro = computed(() => props.config.templates?.cwHandoffIntro || "A mensagem será enviada para");
const handoffInstruction = computed(() => props.config.templates?.cwHandoffInstruction || "Se preferir, entre em contato diretamente: copie nossa conversa pelo botão à esquerda e cole-a no corpo da mensagem para o endereço de e-mail.");
const handoffEmailDisabled = computed(() => props.config.templates?.cwHandoffEmailDisabled || "⚠ Sentimos muito, mas no momento o sistema de e-mail interno para esse serviço está desativado devido à manutenção ou falha interna. Entre em contato diretamente: copie nossa conversa pelo botão à esquerda e cole-a no corpo da mensagem para o endereço de e-mail. Nossa interação está salva no sistema, caso algo dê errado, sem capturar dados além dos básicos previstos na nossa política de privacidade (https://prestech.com.br/site/politica-de-privacidade-prestech/), ou fornecidos diretamente aqui.");

const visibleMessages = computed(() => client.transcript.value);
const compatibilityClasses = computed(() => buildCompatibilityClassList(compatibility.value));
const launcherIconKind = computed(() => launcherIconState.value.kind);
const launcherIconSrc = computed(() =>
  launcherIconState.value.kind === "image" ? launcherIconState.value.src : ""
);

const launcherColorOverrides = computed(() => {
  const style: Record<string, string> = {};
  if (props.config.launcherColor) {
    style['--ptc-chatbot-secondary'] = props.config.launcherColor;
  }
  if (props.config.launcherBorderColor) {
    style['--ptc-launcher-border-color'] = props.config.launcherBorderColor;
  }
  return style;
});

const refreshCompatibility = (): void => {
  if (typeof document !== "undefined") {
    compatibility.value = detectLiveSiteCompatibility(document, compatibilityConfig);
  }
};

const scrollToBottom = async (): Promise<void> => {
  await nextTick();
  if (messagesRef.value) {
    messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
  }
};

const toggle = async (): Promise<void> => {
  open.value = !open.value;
  if (open.value) {
    await client.connect();
    await scrollToBottom();
  }
};

const close = (): void => {
  open.value = false;
};

const send = async (): Promise<void> => {
  const text = draft.value.trim();
  if (!text || client.pending.value) {
    return;
  }

  draft.value = "";
  await client.sendMessage(text);
  await scrollToBottom();
};

const onTextareaKeydown = (event: KeyboardEvent): void => {
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    void send();
  }
};

const copyConversation = async (messageId: string, text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
    copiedMessageId.value = messageId;
    setTimeout(() => {
      if (copiedMessageId.value === messageId) {
        copiedMessageId.value = null;
      }
    }, 2000);
  } catch {
    // Clipboard unavailable — silently ignore.
  }
};

const clear = async (): Promise<void> => {
  await client.clearHistory();
};

watch(
  () => [client.transcript.value.length, client.pending.value],
  () => {
    if (open.value) {
      void scrollToBottom();
    }
  }
);

onMounted(() => {
  refreshCompatibility();
  if (rootRef.value && typeof document !== "undefined") {
    placementCleanup = bindLiveSitePlacement(rootRef.value, document, compatibilityConfig);
  }

  if (typeof MutationObserver !== "undefined" && document.body) {
    compatibilityObserver = new MutationObserver(refreshCompatibility);
    compatibilityObserver.observe(document.body, { childList: true, subtree: true });
  }

  showGreeting.value = true;
  setTimeout(() => {
    showGreeting.value = false;
  }, 5000);
});

onBeforeUnmount(() => {
  compatibilityObserver?.disconnect();
  placementCleanup?.();
});
</script>

<template>
  <section
    ref="rootRef"
    class="ptc-chatbot"
    :class="[{ 'is-open': open }, compatibilityClasses]"
    :style="launcherColorOverrides"
  >
    <button
      id="ptc-launcher-btn"
      class="ptc-chatbot__launcher"
      type="button"
      :title="open ? 'Fechar chatbot da Prestech' : 'Abrir chatbot da Prestech'"
      :aria-label="open ? 'Fechar chatbot da Prestech' : 'Abrir chatbot da Prestech'"
      :aria-expanded="open ? 'true' : 'false'"
      aria-controls="ptc-chatbot-panel"
      @click="toggle"
    >
      <span
        class="ptc-chatbot__launcher-icon"
        :class="`is-${launcherIconKind}`"
        aria-hidden="true"
      >
        <span v-if="launcherIconKind === 'loading'" class="ptc-chatbot__launcher-spinner"></span>
        <img v-else-if="launcherIconKind === 'image'" :src="launcherIconSrc" alt="" />
        <span v-else class="ptc-chatbot__launcher-fallback">
          <sup>P</sup>
          <span>{{ fallbackRobotGlyph }}</span>
        </span>
      </span>
      <span class="ptc-chatbot__status" aria-hidden="true"></span>
    </button>

    <aside
      v-if="showGreeting && !open"
      class="ptc-chatbot__greeting"
      role="status"
    >
      <p>{{ greetingPopup }}</p>
    </aside>

    <section
      v-if="open"
      id="ptc-chatbot-panel"
      class="ptc-chatbot__panel"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="'ptc-chatbot-title'"
    >
      <header class="ptc-chatbot__header">
        <div>
          <h2 id="ptc-chatbot-title">{{ title }}</h2>
          <p>{{ subtitle }}</p>
        </div>
        <button
          id="ptc-close-btn"
          type="button"
          title="Fechar janela do chatbot"
          aria-label="Fechar chatbot"
          @click="close"
        >&times;</button>
      </header>

      <main
        ref="messagesRef"
        class="ptc-chatbot__messages"
        role="log"
        aria-label="Histórico da conversa"
        aria-live="polite"
      >
        <article class="ptc-chatbot__message is-bot">
          <p v-html="renderMessageHtml(initialMessage)"></p>
        </article>

        <article
          v-for="message in visibleMessages"
          :key="message.id"
          class="ptc-chatbot__message"
          :class="{
            'is-bot': message.direction === 'incoming',
            'is-user': message.direction === 'outgoing',
            'is-pending': message.pending
          }"
        >
          <p v-html="renderMessageHtml(message.text)"></p>
          <template v-if="message.meta?.handoffEmail || message.meta?.conversationText">
            <div class="ptc-chatbot__handoff-footer">
              <p v-if="message.meta?.handoffEmail" class="ptc-chatbot__handoff-email">
                <template v-if="message.meta.handoffEmailEnabled">
                  {{ handoffIntro }}
                  <a
                    :href="'mailto:' + message.meta.handoffEmail"
                    :title="'Enviar e-mail para ' + message.meta.handoffEmail"
                    rel="noopener noreferrer nofollow"
                  >{{ message.meta.handoffEmail }}</a>.
                  {{ handoffInstruction }}
                </template>
                <template v-else>{{ handoffEmailDisabled }}</template>
              </p>
              <button
                v-if="message.meta?.conversationText"
                :id="'ptc-copy-' + message.id"
                class="ptc-chatbot__copy-btn"
                :class="{ 'is-copied': copiedMessageId === message.id }"
                :title="copiedMessageId === message.id ? 'Conversa copiada' : 'Copiar conversa para área de transferência'"
                :aria-label="copiedMessageId === message.id ? 'Copiado' : 'Copiar conversa'"
                @click="copyConversation(message.id, message.meta.conversationText)"
              >
                <svg v-if="copiedMessageId === message.id" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-copy" viewBox="0 0 16 16" aria-hidden="true">
                  <path fill-rule="evenodd" d="M4 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1h1v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1v1z"/>
                </svg>
              </button>
            </div>
          </template>
        </article>

        <article
          v-if="client.pending.value"
          class="ptc-chatbot__message is-bot is-thinking"
          role="status"
          aria-label="Aguardando resposta"
        >
          <p><span>{{ thinkingText }}</span></p>
        </article>
      </main>

      <form
        class="ptc-chatbot__composer"
        aria-label="Enviar mensagem"
        @submit.prevent="send"
      >
        <label for="ptc-chatbot-input">Mensagem</label>
        <textarea
          id="ptc-chatbot-input"
          v-model="draft"
          maxlength="1200"
          rows="3"
          title="Digite sua mensagem (máximo 1200 caracteres)"
          placeholder="Digite sua mensagem..."
          :aria-describedby="client.error.value ? 'ptc-chatbot-error' : undefined"
          @keydown="onTextareaKeydown"
        ></textarea>
        <div class="ptc-chatbot__actions">
          <button id="ptc-clear-btn" type="button" class="is-secondary" title="Limpar histórico da conversa" @click="clear">Limpar</button>
          <button id="ptc-send-btn" type="submit" :disabled="!canSend" title="Enviar mensagem">Enviar</button>
        </div>
        <p v-if="client.error.value" id="ptc-chatbot-error" class="ptc-chatbot__error" role="alert">{{ client.error.value }}</p>
      </form>
    </section>
  </section>
</template>
